import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import {
  getTrainingAlertCounties,
  subscribeGuestTrainingGeoAlerts,
  type TeGeoCounty,
  type TeGeoState,
} from "../../services/trainingEventGeoAlertsApi";
import {
  normalizeGuestAlertEmail,
  TRAINING_GEO_ALERT_GUEST_MAX_COUNTIES,
} from "../../../shared/conversion/trainingEventGeoAlertGuest";
import { recordSubscribed } from "../../../shared/conversion/trainingAlertsNudgeFrequency";
import { isViteFirstWinActivationEnabled } from "../../../shared/conversion/conversionProgramFlags";
import {
  FIRST_WIN_DOOR_GUEST_ALERT,
  FIRST_WIN_GUEST_OPEN_EMAIL_HELPER,
  getGuestSuccessActions,
} from "../../../shared/conversion/firstWinActivation";
import { trackConversionEvent } from "../../utils/analytics/conversionTracking";
import {
  CONVERSION_ALERTS_CATEGORY,
  CONVERSION_REGISTER_CATEGORY,
  FIRST_WIN_PRIMARY_CLICK_ACTION,
  FIRST_WIN_PROMPT_SHOWN_ACTION,
  GEO_ALERT_GUEST_SUBMIT_ACTION,
} from "../../../shared/analytics/conversionEvents";

export type TrainingGeoAlertGuestFormProps = {
  states: TeGeoState[];
  statesLoading?: boolean;
  initialGeoStateId?: number | null;
  initialCountyIds?: number[];
  /** Short campaign label persisted with the subscription (e.g. `alerts_landing`). */
  source?: string;
  /** Rendered under the form so guests can still opt into a full account. */
  upgradeSlot?: React.ReactNode;
  onSubscribed?: () => void;
};

/**
 * Email-first (no account) alert signup. Counties are optional — leaving them empty
 * subscribes to the whole state. Double opt-in: submitting only sends a confirm link,
 * so the success state tells the guest to check their inbox.
 */
export default function TrainingGeoAlertGuestForm({
  states,
  statesLoading = false,
  initialGeoStateId = null,
  initialCountyIds = [],
  source,
  upgradeSlot,
  onSubscribed,
}: TrainingGeoAlertGuestFormProps) {
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [selectedState, setSelectedState] = useState<TeGeoState | null>(null);
  const [counties, setCounties] = useState<TeGeoCounty[]>([]);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [selectedCounties, setSelectedCounties] = useState<TeGeoCounty[]>([]);
  const [optIn, setOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [submittedCountyCount, setSubmittedCountyCount] = useState(0);
  const [submittedCountyIds, setSubmittedCountyIds] = useState<number[]>([]);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const promptTrackedRef = useRef(false);

  const firstWinEnabled = isViteFirstWinActivationEnabled({
    VITE_FIRST_WIN_ACTIVATION_ENABLED:
      import.meta.env.VITE_FIRST_WIN_ACTIVATION_ENABLED,
  } as NodeJS.ProcessEnv);

  const successActions = useMemo(
    () =>
      getGuestSuccessActions({
        firstWinEnabled,
        hasUpgradeSlot: Boolean(upgradeSlot),
      }),
    [firstWinEnabled, upgradeSlot],
  );

  useEffect(() => {
    if (initialGeoStateId == null || states.length === 0) return;
    setSelectedState(
      (prev) => prev ?? states.find((s) => s.id === initialGeoStateId) ?? null,
    );
  }, [initialGeoStateId, states]);

  const initialCountyIdsKey = initialCountyIds.join(",");

  // Counties are public reference data, so guests can narrow before signing up.
  useEffect(() => {
    if (!selectedState) {
      setCounties([]);
      setSelectedCounties([]);
      return;
    }
    let cancelled = false;
    setCountiesLoading(true);
    void (async () => {
      try {
        const r = await getTrainingAlertCounties(selectedState.id);
        if (cancelled) return;
        const list = r.data || [];
        setCounties(list);
        const prefill = initialCountyIdsKey
          .split(",")
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n) && n > 0);
        setSelectedCounties(
          prefill.length > 0 ? list.filter((c) => prefill.includes(c.id)) : [],
        );
      } catch {
        if (cancelled) return;
        // County narrowing is optional — fall back to state-wide without an error banner.
        setCounties([]);
        setSelectedCounties([]);
      } finally {
        if (!cancelled) setCountiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedState, initialCountyIdsKey]);

  useEffect(() => {
    if (!submittedEmail || !firstWinEnabled || promptTrackedRef.current) return;
    promptTrackedRef.current = true;
    trackConversionEvent({
      action: FIRST_WIN_PROMPT_SHOWN_ACTION,
      category: CONVERSION_REGISTER_CATEGORY,
      custom_parameters: { door: FIRST_WIN_DOOR_GUEST_ALERT },
    });
  }, [submittedEmail, firstWinEnabled]);

  const normalizedEmail = useMemo(() => normalizeGuestAlertEmail(email), [email]);
  const emailError = emailTouched && email.trim().length > 0 && !normalizedEmail;
  const canSubmit = Boolean(normalizedEmail) && Boolean(selectedState) && optIn && !submitting;

  const handleSubscribe = async (opts?: { resend?: boolean }) => {
    const emailForSend = opts?.resend
      ? normalizeGuestAlertEmail(submittedEmail || email)
      : normalizedEmail;
    if (!emailForSend || !selectedState) return;
    if (opts?.resend) {
      setResending(true);
    } else {
      setSubmitting(true);
    }
    setError(null);
    const countyIds = opts?.resend
      ? submittedCountyIds
      : selectedCounties.map((c) => c.id);
    try {
      await subscribeGuestTrainingGeoAlerts({
        email: emailForSend,
        geo_state_id: selectedState.id,
        ...(countyIds.length > 0 ? { county_ids: countyIds } : {}),
        ...(source ? { source } : {}),
      });
      try {
        recordSubscribed(
          typeof localStorage !== "undefined" ? localStorage : null,
        );
      } catch {
        // ignore storage failures
      }
      if (opts?.resend) {
        setResendNotice("Confirmation email sent again. Check your inbox.");
      } else {
        trackConversionEvent({
          action: GEO_ALERT_GUEST_SUBMIT_ACTION,
          category: CONVERSION_ALERTS_CATEGORY,
          custom_parameters: {
            geo_state_id: selectedState.id,
            county_count: countyIds.length,
            source: source || null,
          },
        });
        setSubmittedEmail(normalizedEmail || emailForSend);
        setSubmittedCountyCount(countyIds.length);
        setSubmittedCountyIds(countyIds);
        setResendNotice(null);
        onSubscribed?.();
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : opts?.resend
            ? "We could not resend the confirmation email. Please try again."
            : "We could not start your alerts. Please try again.",
      );
    } finally {
      setSubmitting(false);
      setResending(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await handleSubscribe();
  };

  const resetSuccess = () => {
    setSubmittedEmail(null);
    setSubmittedCountyCount(0);
    setSubmittedCountyIds([]);
    setOptIn(false);
    setError(null);
    setResendNotice(null);
    promptTrackedRef.current = false;
  };

  if (submittedEmail) {
    if (!firstWinEnabled) {
      return (
        <Stack spacing={2} data-testid="training-geo-alert-guest-success">
          <Alert
            severity="success"
            icon={<MarkEmailReadOutlinedIcon fontSize="inherit" />}
            role="status"
          >
            <AlertTitle>Check your email</AlertTitle>
            <Typography variant="body2">
              We sent a confirmation link to <strong>{submittedEmail}</strong>. Click it to
              start getting alerts for new training in{" "}
              {submittedCountyCount > 0
                ? `${submittedCountyCount} ${
                    submittedCountyCount === 1 ? "county" : "counties"
                  } in ${selectedState?.name || "your state"}`
                : selectedState?.name || "your state"}
              . The link expires in 48 hours.
            </Typography>
          </Alert>
          <Button
            variant="text"
            size="small"
            onClick={resetSuccess}
            data-testid="training-geo-alert-guest-add-another"
            sx={{ alignSelf: "flex-start" }}
          >
            Add another state
          </Button>
          {upgradeSlot}
        </Stack>
      );
    }

    const primary = successActions.find((a) => a.kind === "primary");
    const secondaries = successActions.filter(
      (a) => a.kind === "secondary" && a.id !== "upgrade",
    );

    return (
      <Stack spacing={2} data-testid="training-geo-alert-guest-success">
        <Alert
          severity="success"
          icon={<MarkEmailReadOutlinedIcon fontSize="inherit" />}
          role="status"
        >
          <AlertTitle>Check your email</AlertTitle>
          <Typography variant="body2">
            We sent a confirmation link to <strong>{submittedEmail}</strong> for{" "}
            {submittedCountyCount > 0
              ? `${submittedCountyCount} ${
                  submittedCountyCount === 1 ? "county" : "counties"
                } in ${selectedState?.name || "your state"}`
              : selectedState?.name || "your state"}
            .
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {FIRST_WIN_GUEST_OPEN_EMAIL_HELPER}
          </Typography>
        </Alert>
        {error ? (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        ) : null}
        {resendNotice ? (
          <Alert severity="success" role="status" data-testid="training-geo-alert-guest-resend-ok">
            {resendNotice}
          </Alert>
        ) : null}
        {primary ? (
          <Button
            variant="contained"
            disabled={resending}
            onClick={() => {
              trackConversionEvent({
                action: FIRST_WIN_PRIMARY_CLICK_ACTION,
                category: CONVERSION_REGISTER_CATEGORY,
                custom_parameters: {
                  door: FIRST_WIN_DOOR_GUEST_ALERT,
                  cta_id: primary.id,
                },
              });
              void handleSubscribe({ resend: true });
            }}
            startIcon={
              resending ? (
                <CircularProgress size={16} color="inherit" aria-hidden />
              ) : undefined
            }
            data-testid="training-geo-alert-guest-resend"
            sx={{ alignSelf: "flex-start", fontWeight: 700 }}
          >
            {resending ? "Resending…" : primary.label}
          </Button>
        ) : null}
        {secondaries.map((cta) => (
          <Button
            key={cta.id}
            variant="text"
            size="small"
            onClick={resetSuccess}
            data-testid="training-geo-alert-guest-add-another"
            sx={{ alignSelf: "flex-start" }}
          >
            {cta.label}
          </Button>
        ))}
        {upgradeSlot}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} data-testid="training-geo-alert-guest-form">
      {error ? (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      ) : null}

      {statesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={28} aria-label="Loading states" />
        </Box>
      ) : (
        <Autocomplete
          disablePortal
          options={states}
          getOptionLabel={(o) =>
            [o.name, o.iso2].filter(Boolean).join(" — ") || String(o.id)
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedState}
          onChange={(_, v) => setSelectedState(v)}
          blurOnSelect
          clearOnBlur={false}
          ListboxProps={{ sx: { maxHeight: 280 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="State"
              required
              placeholder="Type to search"
              helperText="Pick a state, then narrow to counties below if you want."
              inputProps={{ ...params.inputProps, "aria-label": "Select state" }}
            />
          )}
        />
      )}

      {selectedState && countiesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={24} aria-label="Loading counties" />
        </Box>
      ) : null}

      {selectedState && !countiesLoading && counties.length > 0 ? (
        <Autocomplete
          disablePortal
          multiple
          options={counties}
          getOptionLabel={(c) => c.name || String(c.id)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedCounties}
          onChange={(_, v) => setSelectedCounties(v.slice(0, TRAINING_GEO_ALERT_GUEST_MAX_COUNTIES))}
          blurOnSelect
          clearOnBlur={false}
          ListboxProps={{ sx: { maxHeight: 280 } }}
          data-testid="training-geo-alert-guest-counties"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Counties (optional)"
              placeholder="Type to search counties"
              helperText={
                selectedCounties.length > 0
                  ? `Alerts limited to ${selectedCounties.length} ${
                      selectedCounties.length === 1 ? "county" : "counties"
                    }. Clear all for the whole state.`
                  : `Leave empty for all of ${selectedState.name || "this state"}.`
              }
              inputProps={{ ...params.inputProps, "aria-label": "Select counties" }}
            />
          )}
        />
      ) : null}

      <TextField
        label="Email address"
        type="email"
        required
        fullWidth
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => setEmailTouched(true)}
        error={emailError}
        helperText={
          emailError
            ? "Enter a valid email address"
            : "We only use this for training alerts you confirm."
        }
        inputProps={{ "aria-label": "Email address", maxLength: 255 }}
        data-testid="training-geo-alert-guest-email"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            inputProps={{ "aria-label": "Opt in to email alerts" }}
          />
        }
        label="Email me when new training is posted in the areas I picked. I can unsubscribe anytime from any alert email."
      />

      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={() => void handleSubmit()}
        startIcon={
          submitting ? (
            <CircularProgress size={16} color="inherit" aria-hidden />
          ) : undefined
        }
        data-testid="training-geo-alert-guest-submit"
        sx={{ alignSelf: "flex-start", fontWeight: 700 }}
      >
        Email me a confirm link
      </Button>

      {upgradeSlot}
    </Stack>
  );
}
